import React from 'react';
import classNames from 'classnames';
import {
  AvailableIcons,
  Button,
  Icon,
  Typography,
  TypographyType,
  TypographyVariant,
} from 'src/components-dummy';
import { Experiment, VariantType } from 'src/services';
import { useStepControlsSync } from '../useStepControlsSync';
import { UseUuidModal } from '../../ExperimentCard/UseUuidModal';
import { CompareVariantModal } from '../../ExperimentForm/CompareVariantModal';
import { ChangesPreview } from '../../../FeatureChanges';
import { PreviewTypeEnum } from '../../../../app-types/enums/versioning';
import { useReviewStep } from './useReviewStep';
import { ReviewStepProps } from './types';
import './ReviewStep.scss';

const STATUS_ICON: Record<string, AvailableIcons> = {
  pass: AvailableIcons.Valid,
  warn: AvailableIcons.WarningOutline,
  blocked: AvailableIcons.Error,
};

export const ReviewStep = (props: ReviewStepProps): JSX.Element => {
  const { experiment, dispatch, onStepControlsChange } = props;
  const {
    summary,
    preflightChecks,
    canLaunch,
    isLaunching,
    launchError,
    showUseUuidDialog,
    setShowUseUuidDialog,
    onLaunchClick,
    onConfirmLaunch,
    onSaveAsDraft,
    comparedVariantId,
    onOpenFullDiff,
  } = useReviewStep(props);

  useStepControlsSync({ canLaunch, isLaunching, onLaunchClick }, onStepControlsChange, [
    canLaunch,
    isLaunching,
    onLaunchClick,
  ]);

  const testVariant = experiment?.variants.find(variant => variant.type === VariantType.Test);

  return (
    <div className='syte-review-step'>
      <div className='syte-review-step-card'>
        <Typography type={TypographyType.Heading} variant={TypographyVariant.SmallBold}>
          {summary.name}
        </Typography>
        <Typography
          type={TypographyType.Paragraph}
          variant={TypographyVariant.MediumRegular}
          className='syte-review-step-hypothesis'
        >
          {summary.hypothesis || 'No hypothesis set.'}
        </Typography>
        <Typography
          type={TypographyType.Body}
          variant={TypographyVariant.SmallRegular}
          className='syte-review-step-sensitivity'
        >
          {summary.sensitivity}
        </Typography>

        <div className='syte-review-step-strip'>
          <div className='syte-review-step-strip-item'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              Surface
            </Typography>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
              {summary.surfaceLabel}
            </Typography>
          </div>
          <div className='syte-review-step-strip-item'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              Deciding metric
            </Typography>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
              {summary.metricLabel}
            </Typography>
          </div>
          <div className='syte-review-step-strip-item'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              Split
            </Typography>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
              {summary.splitLabel}
            </Typography>
          </div>
          <div className='syte-review-step-strip-item'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              Minimum duration
            </Typography>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
              {summary.minimumDurationLabel}
            </Typography>
          </div>
        </div>
      </div>

      <div className='syte-review-step-card'>
        <div className='syte-review-step-card-header'>
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
            What&apos;s different in Variant B
          </Typography>
          <button type='button' className='syte-review-step-link' onClick={onOpenFullDiff}>
            Open full diff
          </button>
        </div>
        {testVariant?.versionChangesSummary?.changedEntities ? (
          <ChangesPreview
            changedEntities={testVariant.versionChangesSummary.changedEntities}
            previewType={PreviewTypeEnum.COMPARE_VARIANT}
            newValueCustomName={testVariant.name}
            readOnly
          />
        ) : (
          <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
            No changes recorded for Variant B yet.
          </Typography>
        )}
      </div>

      <div className='syte-review-step-card'>
        <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
          Pre-flight checks
        </Typography>
        <div className='syte-review-step-checks'>
          {preflightChecks.map(check => (
            <div key={check.id} className='syte-review-step-check-row'>
              <Icon
                name={STATUS_ICON[check.status]}
                className={classNames(
                  'syte-review-step-check-icon',
                  `syte-review-step-check-icon-${check.status}`
                )}
              />
              <div>
                <Typography
                  type={TypographyType.Body}
                  variant={TypographyVariant.SmallBold}
                  className='syte-review-step-check-label'
                >
                  {check.label}
                </Typography>
                <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                  {check.description}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className='syte-review-step-footer'>
        {launchError && (
          <Typography
            type={TypographyType.Body}
            variant={TypographyVariant.SmallRegular}
            className='syte-review-step-launch-error'
          >
            {launchError}
          </Typography>
        )}
        <div className='syte-review-step-footer-actions'>
          <Button variant='primary' disabled={!canLaunch || isLaunching} onClick={onLaunchClick}>
            🚀 Launch experiment
          </Button>
          <Button
            variant='secondary'
            className='syte-review-step-save-draft-button'
            onClick={onSaveAsDraft}
          >
            Save as draft
          </Button>
        </div>
        <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
          Traffic starts splitting within about 5 minutes of launch.
        </Typography>
      </div>

      {showUseUuidDialog && (
        <UseUuidModal
          onRunExperiment={onConfirmLaunch}
          setShowUseUuidDialog={setShowUseUuidDialog}
        />
      )}

      <CompareVariantModal
        comparedVariantId={comparedVariantId}
        experiment={experiment as Experiment | undefined}
        dispatch={dispatch}
      />
    </div>
  );
};
